// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState, useMemo, useCallback } from 'react';

import { type WorkOrderItem, useEvolvePaginatedGet, type WithRequired } from '@evolvemep/api-ts';
import { Flex, Text, Stack } from '@mantine/core';
import { AgGridReact } from 'ag-grid-react';

import { AgGridEditable } from 'helpers/ag-grid/EditableCells/AgGridEditable';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { isNotNil } from 'helpers/isNotNil';
import { getWorkOrderItemBomColDef } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/columnDefs';
import {
  AddWorkOrderItemsFromCatalogModal,
  type AddWorkOrderItemsFromCatalogModalProps,
} from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/AddWorkOrderItemsFromCatalogModal';
import { AddWorkOrderWriteInItemButton } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/AddWorkOrderWriteInItemButton';
import { DeleteWorkOrderItem } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/DeleteWorkOrderItem';
import { useBomItemEditing } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/useBomItemEditing';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

type Props = WithRequired<
  Pick<
    AddWorkOrderItemsFromCatalogModalProps,
    'opened' | 'onClose' | 'workOrder' | 'parentWorkOrderItem' | 'onItemsChanged'
  >,
  'parentWorkOrderItem'
>;

export const AddBOMItemsFromCatalogModal = ({ onItemsChanged: onItemsChangedParent, ...props }: Props) => {
  const { workOrder, parentWorkOrderItem } = props;
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);

  const { fetchPage, abortAll } = useEvolvePaginatedGet('shop/workOrder/item', {
    defaultConfig: {
      params: {
        workOrderId: parentWorkOrderItem.workOrderId,
        parentWorkOrderItemId: parentWorkOrderItem.workOrderItemId,
      },
    },
  });

  const [deletingItem, setDeletingItem] = useState<WorkOrderItem | null>(null);

  const { agGridEditableProps, updateItem } = useBomItemEditing(onItemsChangedParent);

  const colDef = useMemo(
    () =>
      getWorkOrderItemBomColDef({
        parentItem: parentWorkOrderItem,
        canEditItems: canEditWorkOrder('editItems'),
        updateItem,
        onDeleteItem: setDeletingItem,
        compact: true,
      }),
    [canEditWorkOrder, parentWorkOrderItem, updateItem],
  );

  const { agGridProps, gridApi } = useServerSideGrid({
    tableName: 'work-order-item-bom',
    fetchPage,
    abortAll,
    colDef,
    rowId: 'workOrderItemId',
  });

  const onItemsChanged = useCallback<typeof onItemsChangedParent>(() => {
    refreshGrid(gridApi);
    onItemsChangedParent();
  }, [gridApi, onItemsChangedParent]);

  return (
    <>
      <AddWorkOrderItemsFromCatalogModal {...props} onItemsChanged={onItemsChanged}>
        {() => (
          <>
            <Stack gap={0}>
              <Flex justify="space-between" align="center">
                <Text fw="bold">{isNotNil(parentWorkOrderItem) ? <>Bill of materials</> : <>Work order items</>}</Text>
                <AddWorkOrderWriteInItemButton
                  workOrder={workOrder}
                  parentWorkOrderItem={parentWorkOrderItem}
                  onItemAdded={onItemsChanged}
                />
              </Flex>
            </Stack>
            <AgGridEditable gridApi={gridApi} {...agGridEditableProps}>
              {(applyWrap) => <AgGridReact {...agGridProps} onFirstDataRendered={applyWrap} />}
            </AgGridEditable>
          </>
        )}
      </AddWorkOrderItemsFromCatalogModal>

      <DeleteWorkOrderItem
        workOrderItem={deletingItem}
        onClose={() => setDeletingItem(null)}
        onDeleted={onItemsChanged}
      />
    </>
  );
};
